/**
 * @file TextSizeContext.jsx
 * @description Global context for managing text size across the app
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

const TextSizeContext = createContext();

export const useTextSize = () => {
  const context = useContext(TextSizeContext);
  if (!context) {
    throw new Error('useTextSize must be used within a TextSizeProvider');
  }
  return context;
};

// Text size utility functions
export const getTextSizeValue = (size) => {
  const sizes = {
    small: '14px',
    medium: '16px',
    large: '18px'
  };
  return sizes[size] || sizes.medium;
};

export const getTextSizes = (size) => {
  const sizes = {
    small: {
      button: '12px',
      body: '14px',
      heading: '18px',
      title: '20px',
      buttonHeight: '28px'
    },
    medium: {
      button: '14px',
      body: '16px',
      heading: '20px',
      title: '24px',
      buttonHeight: '32px'
    },
    large: {
      button: '16px',
      body: '18px',
      heading: '24px',
      title: '28px',
      buttonHeight: '36px'
    }
  };
  return sizes[size] || sizes.medium;
};

// Verse text sizes — heading/body only (chapter headings and verse text)
export const getVerseTextSizes = (size) => {
  const sizes = {
    small: {
      body: '14px',
      heading: '18px',
    },
    medium: {
      body: '16px',
      heading: '20px',
    },
    large: {
      body: '20px',
      heading: '26px',
    },
    xlarge: {
      body: '24px',
      heading: '30px',
    }
  };
  return sizes[size] || sizes.medium;
};

// Apply app text size to CSS custom properties for global effect
const applyTextSizeToDocument = (textSize) => {
  const textSizes = getTextSizes(textSize);

  // Update CSS custom properties for global typography
  document.documentElement.style.setProperty('--app-text-size-small', textSizes.button);
  document.documentElement.style.setProperty('--app-text-size-body', textSizes.body);
  document.documentElement.style.setProperty('--app-text-size-heading', textSizes.heading);
  document.documentElement.style.setProperty('--app-text-size-title', textSizes.title);
  document.documentElement.style.setProperty('--app-text-size-button-height', textSizes.buttonHeight);

  // Also update the base font size for general scaling
  document.documentElement.style.setProperty('--app-base-font-size', getTextSizeValue(textSize));
};

const VALID_SIZES = ['small', 'medium', 'large'];
const VALID_VERSE_SIZES = ['small', 'medium', 'large', 'xlarge'];

const saveCookie = (key, value) => {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${key}=${value}; expires=${expires.toUTCString()}; path=/`;
};

const readCookie = (key) => {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(`${key}=`))
    ?.split('=')[1];
};

export const TextSizeProvider = ({ children }) => {
  const [textSize, setTextSize] = useState('medium');
  const [verseTextSize, setVerseTextSize] = useState('medium');

  // Load text sizes from cookies on mount
  useEffect(() => {
    const savedTextSize = readCookie('textSize');
    if (savedTextSize && VALID_SIZES.includes(savedTextSize)) {
      setTextSize(savedTextSize);
    }

    const savedVerseTextSize = readCookie('verseTextSize');
    if (savedVerseTextSize && VALID_VERSE_SIZES.includes(savedVerseTextSize)) {
      setVerseTextSize(savedVerseTextSize);
    }
  }, []);

  // Apply app text size changes to document
  useEffect(() => {
    applyTextSizeToDocument(textSize);
  }, [textSize]);

  const changeTextSize = (newSize) => {
    if (!VALID_SIZES.includes(newSize)) {
      console.warn('Invalid text size:', newSize);
      return;
    }
    setTextSize(newSize);
    saveCookie('textSize', newSize);
  };

  const changeVerseTextSize = (newSize) => {
    if (!VALID_VERSE_SIZES.includes(newSize)) {
      console.warn('Invalid verse text size:', newSize);
      return;
    }
    setVerseTextSize(newSize);
    saveCookie('verseTextSize', newSize);
  };

  const value = {
    textSize,
    changeTextSize,
    verseTextSize,
    changeVerseTextSize,
    getTextSizeValue: () => getTextSizeValue(textSize),
    getTextSizes: () => getTextSizes(textSize),
    getVerseTextSizes: () => getVerseTextSizes(verseTextSize),
  };

  return (
    <TextSizeContext.Provider value={value}>
      {children}
    </TextSizeContext.Provider>
  );
};
